<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { Toaster, type ToasterProps } from 'vue-sonner';
import 'vue-sonner/style.css';
import { usePlatform } from '@core/platform';
import { useGetCurrentUserUsersMeGet } from '@gen/hooks';
import { i18n, resolveStoredLocale } from '@app/i18n';
import { useTheme } from '@app/composables/useTheme';
import { useAppInit } from '@app/composables/useAppInit';
import { useNavigation } from '@app/composables/useNavigation';
import { useResolvedLayout } from '@app/composables/useResolvedLayout';
import AppShell from '@app/presentation/components/AppShell.vue';
import SiteLayout from '@app/presentation/components/site/SiteLayout.vue';
import { HOME_ROUTE } from '@app/router/router';
import DebugPanel from '@app/presentation/components/DebugPanel.vue';
import ErrorScreen from '@app/presentation/components/shared/ErrorScreen.vue';
import LoadingScreen from '@app/presentation/components/shared/LoadingScreen.vue';
import Paywall from '@app/presentation/screens/payments/Paywall.vue';

// Critical above-the-fold fonts, preloaded to avoid FOUT. @fontsource ships
// `font-display: swap` with no preload, so the pre-rendered page first paints
// in the system fallback (Georgia for the serif headings, system sans for body)
// and visibly swaps once the woff2 files download. We preload only the subsets
// that render the hero on the Russian-first marketing pages — Tenor Sans latin
// for the "Tarot Meow" wordmark, plus the cyrillic body/heading faces — per
// Fontsource's "preload only critical subsets" guidance. `?url` resolves to the
// content-hashed asset URL at build time, so the <link> always stays correct.
import tenorSansLatin400 from '@fontsource/tenor-sans/files/tenor-sans-latin-400-normal.woff2?url';
import tenorSansCyrillic400 from '@fontsource/tenor-sans/files/tenor-sans-cyrillic-400-normal.woff2?url';
import interCyrillic400 from '@fontsource/inter/files/inter-cyrillic-400-normal.woff2?url';

const platform = usePlatform();
const { isTelegram, showBackButton, hideBackButton } = platform;
const route = useRoute();
const router = useRouter();
const { goUp } = useNavigation();

// Single source of layout truth. Called ONCE here (it registers the app's only
// useMediaQuery/useHydrated instance) and threaded into AppShell as a prop.
const layout = useResolvedLayout();
const ShellComponent = computed(() => (layout.value.shell === 'site' ? SiteLayout : AppShell));

// App initialization state
const { isLoading, error } = useAppInit();
const isSSR = import.meta.env.SSR;

// Responsive toast position: top-center on mobile, bottom-right on desktop
const toastPosition = ref<ToasterProps['position']>('top-center');
const DESKTOP_BREAKPOINT = 768;

// Theme mode — always dark
const mode = useTheme();
mode.value = 'dark';
const toastTheme = 'dark' as const;

function updateToastPosition() {
  toastPosition.value = window.innerWidth >= DESKTOP_BREAKPOINT ? 'bottom-right' : 'top-center';
}

onMounted(() => {
  updateToastPosition();
  window.addEventListener('resize', updateToastPosition);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateToastPosition);
});

// Language sync: Watch user data and update i18n locale
// Skip for guests - they use localStorage preference instead
const { data: user } = useGetCurrentUserUsersMeGet();
watchEffect(() => {
  const newUser = user.value;
  // Don't override locale for guest users - they manage via localStorage
  if (newUser?.user_type === 'GUEST') {
    return;
  }
  if (newUser?.language_code) {
    const lang = newUser.language_code.toLowerCase();
    const supportedLocales = ['en', 'ru'];
    if (supportedLocales.includes(lang)) {
      i18n.global.locale.value = lang as 'en' | 'ru';
    }
  }
});

// Apply the guest's stored/browser locale preference AFTER the initial mount.
// i18n starts on the site default ('ru') so the client's first render matches
// the pre-rendered HTML and Vue hydrates in place; switching the reactive
// locale here is a plain text patch that does not replay entrance animations.
// REGISTERED users keep getting their language_code via the watchEffect above,
// so we only apply the browser preference for guests / not-yet-logged-in users.
// On web, guests without an explicit choice stay on 'ru' (see resolveStoredLocale).
onMounted(() => {
  if (user.value?.user_type === 'REGISTERED') {
    return;
  }
  const preferred = resolveStoredLocale({ isTelegram });
  if (preferred && preferred !== i18n.global.locale.value) {
    i18n.global.locale.value = preferred;
  }
});

// Keep <html lang> in sync with the active i18n locale.
// useHead is reactive, so passing a getter updates the attribute when the
// locale changes. During SSR/SSG pre-render the locale defaults to 'ru'
// (site default), so static output is rendered with lang="ru" for RU SEO.
useHead({
  htmlAttrs: {
    lang: () => i18n.global.locale.value,
  },
  link: [
    { rel: 'preload', as: 'font', type: 'font/woff2', href: tenorSansLatin400, crossorigin: 'anonymous' },
    { rel: 'preload', as: 'font', type: 'font/woff2', href: tenorSansCyrillic400, crossorigin: 'anonymous' },
    { rel: 'preload', as: 'font', type: 'font/woff2', href: interCyrillic400, crossorigin: 'anonymous' },
  ],
});


// Native Telegram BackButton — driven off route meta. Shown on immersive
// (chrome: 'bare') routes that declare a semantic parent and are not the home
// route; hidden everywhere else (tabs / plain / desktop). Step changes are
// query navigations, so afterEach fires for them too and the button persists
// across a wizard's steps. `backShown` tracks whether a callback is currently
// set, because the core hideBackButton() warns + early-returns if called with
// none (e.g. first load straight onto a tabs page).
let backShown = false;
function syncBackButton() {
  // Web/PWA uses the in-app FlowHeader chevron, never the Telegram button.
  if (!isTelegram) return;

  const wantBack =
    route.meta.chrome === 'bare' &&
    typeof route.meta.parent === 'string' &&
    route.path !== HOME_ROUTE;

  if (wantBack) {
    showBackButton(goUp);
    backShown = true;
  } else if (backShown) {
    hideBackButton();
    backShown = false;
  }
}

// Watch for route changes (incl. ?step query navigations).
router.afterEach(() => {
  syncBackButton();
});

// Theme is managed by theme store
// No need to set it here

function onBeforeSegue(): void {
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
  });
}
</script>

<template>
  <div class="paper-grain min-h-svh flex flex-col bg-background text-foreground">
    <!-- Error state (client-only — SSR always renders content for crawlers) -->
    <ErrorScreen v-if="!isSSR && error" :error="error" />

    <!-- Loading state (client-only) -->
    <LoadingScreen v-else-if="!isSSR && isLoading" />

    <!-- Ready state (always rendered during SSR for pre-rendering) -->
    <template v-else>
      <!-- Client-only: vue-sonner's Toaster renders nothing on the server but a
           real <section> on the client. As the first child of this branch, that
           SSR/client structural mismatch desyncs hydration for every sibling
           below (AppShell → hero), forcing a full client re-render that replays
           the entrance animations. Keeping it out of hydration fixes that. -->
      <ClientOnly>
        <Toaster
          :position="toastPosition"
          :theme="toastTheme"
          :toast-options="{ duration: 4000 }"
        />
      </ClientOnly>
      <!-- Client-only: its inner content differs between SSR and client (mock
           toggle state, user <select>), so keep it out of hydration entirely
           to avoid desyncing the pre-rendered marketing DOM. -->
      <ClientOnly>
        <DebugPanel />
      </ClientOnly>
      <!-- One shell, one RouterView+transition. The shell is resolved from the
           route's meta: site routes ('/', /rasklady, /ceny, /faq, spread detail)
           get SiteLayout (unified SiteNav + SiteFooter, no AppNav); app routes
           get AppShell → AppNav. AppShell is prop-driven off the resolved layout;
           SiteLayout ignores it, so pass undefined to avoid a stray attribute. -->
      <component
        :is="ShellComponent"
        :layout="layout.shell === 'site' ? undefined : layout"
        :variant="layout.shell === 'site' ? undefined : route.meta.width"
      >
        <RouterView v-slot="{ Component }">
          <transition
            name="default-segue"
            @before-enter="onBeforeSegue"
          >
            <!-- Key by path, NOT fullPath: query-only changes (the wizard's
                 ?step=N advances) must reuse the component, not remount it —
                 remounting wipes the in-flow state (e.g. the typed question) and
                 the useWizard guard then bounces back to step 0. -->
            <div
              :key="$route.path"
              class="h-full flex flex-col"
            >
              <component :is="Component" />
            </div>
          </transition>
        </RouterView>
      </component>
      <Paywall />
    </template>
  </div>
</template>

<style>
/* Base styles */
html,
body {
  margin: 0;
  padding: 0;
  min-height: 100vh; /* fallback */
  min-height: 100svh;
}

#app {
  min-height: 100vh; /* fallback */
  min-height: 100svh;
  width: 100%;
}

/* Transition styles */
.default-segue-leave-active {
  visibility: hidden;
  height: 0;
  overflow: hidden;
}

.default-segue-enter-active {
  transition: opacity 500ms ease;
  will-change: opacity;
}

.default-segue-enter-from,
.default-segue-leave-to {
  opacity: 0;
}

</style>
